import React from "react";

// NetworkHandler.js - Utilitaires pour la gestion des requêtes réseau avec retry et timeout

export class NetworkError extends Error {
  constructor(type, message, details = null, originalError = null) {
    super(message);
    this.name = 'NetworkError';
    this.type = type;
    this.details = details;
    this.originalError = originalError;
  }
}

/**
 * Wrapper pour fetch avec timeout, retry automatique et gestion d'erreurs améliorée
 */
export const safeFetch = async (url, options = {}, config = {}) => {
  const {
    timeout = 10000,
    retries = 0,
    retryDelay = 1000,
    retryBackoff = true
  } = config;

  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Créer une promesse avec timeout
      const fetchWithTimeout = new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new NetworkError('TIMEOUT', `Request timed out after ${timeout}ms`));
        }, timeout);

        fetch(url, options)
          .then(response => {
            clearTimeout(timeoutId);
            resolve(response);
          })
          .catch(error => {
            clearTimeout(timeoutId);
            reject(error);
          });
      });

      const response = await fetchWithTimeout;

      // Vérifier le statut de la réponse
      if (!response.ok) {
        let errorType;
        let errorMessage;

        if (response.status === 404) {
          errorType = 'NOT_FOUND';
          errorMessage = 'Resource not found';
        } else if (response.status === 403) {
          errorType = 'FORBIDDEN';
          errorMessage = 'Access forbidden';
        } else if (response.status === 401) {
          errorType = 'UNAUTHORIZED';
          errorMessage = 'Unauthorized access';
        } else if (response.status >= 500) {
          errorType = 'SERVER_ERROR';
          errorMessage = 'Server error';
        } else if (response.status >= 400) {
          errorType = 'CLIENT_ERROR';
          errorMessage = 'Client error';
        } else {
          errorType = 'HTTP_ERROR';
          errorMessage = `HTTP ${response.status}`;
        }

        throw new NetworkError(
          errorType,
          errorMessage,
          `${response.status}: ${response.statusText}`
        );
      }

      return response;

    } catch (error) {
      lastError = error;

      // Si c'est la dernière tentative, lancer l'erreur
      if (attempt === retries) {
        if (error instanceof NetworkError) {
          throw error;
        } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
          throw new NetworkError(
            'NETWORK_ERROR',
            'Network connection failed',
            'Check your internet connection',
            error
          );
        } else {
          throw new NetworkError(
            'UNKNOWN_ERROR',
            error.message || 'Unknown network error',
            null,
            error
          );
        }
      }

      // Attendre avant la prochaine tentative
      if (attempt < retries) {
        const delay = retryBackoff ? retryDelay * Math.pow(2, attempt) : retryDelay;
        await new Promise(resolve => setTimeout(resolve, Math.min(delay, 5000)));
      }
    }
  }

  throw lastError;
};

/**
 * Wrapper pour les requêtes JSON avec gestion d'erreurs
 */
export const fetchJSON = async (url, options = {}, config = {}) => {
  try {
    const response = await safeFetch(url, options, config);
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new NetworkError(
        'INVALID_RESPONSE',
        'Response is not valid JSON',
        `Expected JSON, got ${contentType}`
      );
    }

    const data = await response.json();
    return data;

  } catch (error) {
    if (error instanceof NetworkError) {
      throw error;
    } else if (error instanceof SyntaxError) {
      throw new NetworkError(
        'INVALID_JSON',
        'Invalid JSON response',
        'Server returned malformed JSON',
        error
      );
    } else {
      throw new NetworkError(
        'UNKNOWN_ERROR',
        error.message || 'Unknown error while parsing JSON',
        null,
        error
      );
    }
  }
};

/**
 * Utilitaire pour les requêtes POST avec JSON
 */
export const postJSON = async (url, data = {}, config = {}) => {
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  };

  return await fetchJSON(url, options, config);
};

/**
 * Utilitaire pour les requêtes GET avec timeout
 */
export const getJSON = async (url, config = {}) => {
  const options = {
    method: 'GET'
  };

  return await fetchJSON(url, options, config);
};

/**
 * Détecte le type de connexion réseau (si supporté)
 */
export const getConnectionInfo = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      type: connection.type,
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData
    };
  }
  return null;
};

/**
 * Vérifie si la connexion semble lente
 */
export const isSlowConnection = () => {
  const connection = getConnectionInfo();
  if (!connection) return false;

  return (
    connection.effectiveType === 'slow-2g' ||
    connection.effectiveType === '2g' ||
    connection.downlink < 1 ||
    connection.rtt > 1000
  );
};

/**
 * Configuration adaptative du timeout basée sur la connexion
 */
export const getAdaptiveTimeout = (baseTimeout = 10000) => {
  if (isSlowConnection()) {
    return baseTimeout * 2; // Double le timeout pour les connexions lentes
  }
  return baseTimeout;
};

/**
 * Hook pour vérifier l'état de la connexion
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

/**
 * Gestionnaire d'erreurs réseau contextualisé pour l'application
 */
export const handleNetworkError = (error, context = '') => {
  console.error(`Network error in ${context}:`, error);

  // Logger l'erreur avec plus de contexte
  if (error instanceof NetworkError) {
    const errorInfo = {
      type: error.type,
      message: error.message,
      details: error.details,
      context: context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      connection: getConnectionInfo()
    };

    // Dans un environnement de production, on pourrait envoyer ces données à un service de monitoring
    if (process.env.NODE_ENV === 'development') {
      console.table(errorInfo);
    }
  }

  return error;
};

export default {
  safeFetch,
  fetchJSON,
  postJSON,
  getJSON,
  NetworkError,
  getConnectionInfo,
  isSlowConnection,
  getAdaptiveTimeout,
  useNetworkStatus,
  handleNetworkError
};